import classNames from 'classnames';
import React, { useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { focusIsOnControl } from '../../../services/shortcuts/keys';
import useShortcut from '../../../services/shortcuts/useShortcut';
import s from './dialog.module.scss';

/**
 * The one dialog shell: a scrim, a panel, `role="dialog"`, and the three
 * ways out that every dialog owes the user - Escape, a click on the scrim,
 * and whatever button it puts in its own body. Rendered into document.body
 * so it sits above the virtualised lists, which clip and transform their
 * children.
 *
 * Escape goes through the shortcut registry, which is what makes it land on
 * the *topmost* dialog: this one registered when it mounted, later than
 * anything behind it, and an info bubble opened on top of it outranks it by
 * priority. Nothing here has to know about either.
 *
 * With `onSubmit` the panel is a `<form>`, so Enter in any field submits it
 * and the first `type="submit"` button is the default action - and Enter
 * with nothing focused presses that button too, a disabled one included,
 * which does nothing. Without `onSubmit`, Enter does whatever the focused
 * control does. Either way Enter stops at the dialog: whatever the page
 * behind would do with it, it does not do it through a dialog.
 */

interface Props {
  title: React.ReactNode;
  /** An id for the heading, for tests and `aria-labelledby`. Generated when absent. */
  titleId?: string;
  onClose(): void;
  /**
   * False while the dialog must not go away - a job is running inside it and
   * would have nothing to report to. Escape and the scrim then do nothing.
   */
  closable?: boolean;
  /** Makes the panel a form: Enter submits, and this receives it. */
  onSubmit?(ev: React.FormEvent<HTMLFormElement>): void;
  /** Large is for a dialog that shows a list: the iTunes import, the shortcuts. */
  size?: 'medium' | 'large';
  className?: string;
  children: React.ReactNode;
}

let nextId = 0;

export default function Dialog({
  title,
  titleId,
  onClose,
  closable = true,
  onSubmit,
  size = 'medium',
  className,
  children,
}: Props) {
  const [generatedId] = useState(() => `dialog-title-${++nextId}`);
  const headingId = titleId || generatedId;
  const panelRef = useRef<HTMLElement>(null);

  useShortcut({
    id: 'dialog-close',
    keys: { key: 'Escape' },
    group: 'Dialogs',
    description: 'Close the dialog',
    whileTyping: true,
    run: () => {
      if (!closable) {
        return false;
      }

      onClose();
    },
  });

  useShortcut({
    id: 'dialog-confirm',
    keys: { key: 'Enter' },
    group: 'Dialogs',
    description: 'Confirm the dialog',
    run: () => {
      // A focused button or link takes its own Enter.
      if (focusIsOnControl()) {
        return false;
      }

      const primary = panelRef.current?.querySelector<HTMLButtonElement>('button[type="submit"]');

      if (primary) {
        primary.click();
      }
    },
  });

  const panelClassName = classNames(s.panel, { [s.large]: size === 'large' }, className);

  const inner = (
    <>
      <h5 id={headingId} className={s.heading}>
        {title}
      </h5>
      <div className={s.body}>{children}</div>
    </>
  );

  return ReactDOM.createPortal(
    <div
      className={s.overlay}
      onMouseDown={(ev) => {
        if (ev.target === ev.currentTarget && closable) {
          onClose();
        }
      }}
    >
      {onSubmit ? (
        <form
          ref={panelRef as React.RefObject<HTMLFormElement>}
          className={panelClassName}
          onSubmit={onSubmit}
          role="dialog"
          aria-modal="true"
          aria-labelledby={headingId}
        >
          {inner}
        </form>
      ) : (
        <section
          ref={panelRef}
          className={panelClassName}
          role="dialog"
          aria-modal="true"
          aria-labelledby={headingId}
        >
          {inner}
        </section>
      )}
    </div>,
    document.body,
  );
}
